// Central UI state — mirrors the design's this.state. One object updated through a
// merge reducer (the React analogue of the design's setState({...patch})).

import type { Bundle, ConditionKey, ReframeResult } from "./types";
import { readRoute } from "./router";

export interface UIState {
  cond: ConditionKey;
  highlightStyle: "fill" | "underline" | "bracket";
  diffStyle: "inline" | "sidebyside";
  readView: "original" | "edit"; // document-view tab (only when P5 present)
  layout: "margins" | "stacked" | "compact";
  highlightsOn: boolean;
  pinnedHl: string | null;
  tipPos: { top: number; left: number };
  revealCross: boolean;
  openClaims: Record<string, boolean>;
  activeClaim: string | null;
  activeAxis: string | null;
  activeChange: number | null;
  contextOpen: boolean;
  devOpen: boolean;
  acked: boolean;
  skipped: boolean;
  factFilter: "all" | "threat" | "human" | "neutral";
  dvValue: number | null;
  narrow: boolean;
  groundedClaim: string | null;
  flashAxis: string | null;
  sourceView: string | null; // outlet domain shown in the read-only source viewer (null = closed)
  // reframe (P7)
  reframeProfile: string | null;
  advancedOpen: boolean;
  sliderTargets: Record<string, number> | null;
  structTargets: Record<string, string> | null;
  reframeChangeOpen: number | null;
  radarSpoke: string | null;
  liveLoading: boolean;
  liveIter: number;
  liveResult: ReframeResult | null;
  liveCalls: number;
  customActive: boolean;
}

export function initialState(b: Bundle): UIState {
  const rf = b.reframe;
  const dp = rf.precomputed[rf.default_profile_id];
  // Deep-link boot: an empty/invalid hash falls back to the historical defaults,
  // so booting without a hash behaves exactly as before routing existed.
  const rt = readRoute();
  return {
    cond: rt.cond ?? "C4",
    highlightStyle: "fill",
    diffStyle: "inline",
    readView: "original",
    layout: "margins",
    highlightsOn: true,
    pinnedHl: null,
    tipPos: { top: 120, left: 120 },
    revealCross: false,
    openClaims: {},
    activeClaim: null,
    activeAxis: null,
    activeChange: null,
    contextOpen: false,
    devOpen: false,
    acked: false,
    skipped: false,
    factFilter: "all",
    dvValue: null,
    narrow: false,
    groundedClaim: null,
    flashAxis: null,
    sourceView: rt.source,
    reframeProfile: rf.default_profile_id,
    advancedOpen: false,
    sliderTargets: dp ? { ...dp.requested_target } : {},
    structTargets: dp ? { ...dp.structural_targets } : {},
    reframeChangeOpen: null,
    radarSpoke: null,
    liveLoading: false,
    liveIter: 0,
    liveResult: null,
    liveCalls: 0,
    customActive: false,
  };
}

export type Patch = Partial<UIState>;
export function reducer(s: UIState, patch: Patch): UIState {
  return { ...s, ...patch };
}
